import React, { useState, useCallback } from 'react';
import _ from 'lodash';

import { LayoutPageHeading, LayoutCardSize } from 'src/modules/layout';
import { ArticleListingOverview, ArticleLinkPrefix } from 'src/modules/articles';
import LayoutPage from 'src/modules/layout/components/page/LayoutPage';
import { useIntl } from 'react-intl';
import { Form } from 'antd';
import { useLocale } from 'src/modules/locale';
import styles from './ProfessionalsReferencesListingPage.module.scss';
import { FormFilters } from 'src/modules/form';
import { ReferenceCategoryEntity, ReferenceEntity } from 'src/modules/professionals';
import { LocationTypeLocalizedEntity } from 'src/modules/locationTypes';

interface ProfessionalsReferencesListingPageProps {
  projectsPage: {
    references: ReferenceEntity[];
    referenceCategories: ReferenceCategoryEntity[];
    locationTypes: LocationTypeLocalizedEntity[];
  };
}

const filterReferences = (filters: { categories: any; locationTypes: any }, references: any[]) => {
  let filteredReferences = [];

  const isCategories = (reference: any) => {
    // No categories selected
    if (_.isEmpty(filters.categories)) return true;

    // One ore more categories selected
    return _.some(filters.categories, (filterId) => {
      return filterId === reference?.reference_category?.id;
    });
  };

  const isLocationTypes = (reference: any) => {
    // No location types selected
    if (_.isEmpty(filters.locationTypes)) return true;

    // One ore more location types selected
    return _.some(filters.locationTypes, (filterId) =>
      _.some(reference.location_types, (locationType) => locationType?.id === filterId)
    );
  };

  filteredReferences = _.filter(
    references,
    (reference) => isCategories(reference) && isLocationTypes(reference)
  );

  return filteredReferences;
};

const ProfessionalsReferencesListingPage: React.FunctionComponent<ProfessionalsReferencesListingPageProps> = (
  props
) => {
  const intl = useIntl();
  const { getLocalizedObject } = useLocale();
  const [form] = Form.useForm();

  const pageProperties = {
    title: intl.formatMessage({ id: 'page/professionals/projects/listing/head/page_title' }),
    description: intl.formatMessage({
      id: 'page/professionals/projects/listing/head/meta_description',
    }),

    breadcrumbs: [
      {
        title: intl.formatMessage({ id: 'breadcrumbs/professionals' }),
        as: '/professionals',
        href: '/professionals',
      },
      {
        title: intl.formatMessage({ id: 'breadcrumbs/professionals/projects' }),
        as: '/professionals/projects',
        href: '/professionals/projects',
      },
    ],
  };

  const { projectsPage } = getLocalizedObject<ProfessionalsReferencesListingPageProps>(props);
  const {
    references: allReferences,
    referenceCategories: rawReferenceCategories,
    locationTypes: rawLocationTypes,
  } = projectsPage;
  const [references, setReferences] = useState(allReferences);
  const referenceCategories = _.sortBy(rawReferenceCategories, (c) => c?.name);
  const locationTypes = _.sortBy(rawLocationTypes, (l) => l?.name);

  const setFilter = useCallback(
    (filters: any) => {
      const categories = filters?.categories;
      const locationTypes = filters?.locationTypes;

      if (_.isEmpty(categories) && _.isEmpty(locationTypes)) {
        setReferences(allReferences);
        return;
      }

      const filteredReferences = filterReferences({ categories, locationTypes }, allReferences);
      setReferences(filteredReferences);
    },
    [allReferences]
  );

  //Save the location types which have references to display them in the filters
  const availableLocationTypes = (references.length
    ? [
        ...new Set(
          references.reduce(
            (acc: any, reference: any) =>
              acc.concat(reference.location_types.map((locType: any) => locType?.id)),
            []
          )
        ),
      ]
    : []) as string[];

  //Save the reference categories which have references to display them in the filters
  const availableReferenceCategories = (references.length
    ? [
        ...new Set(
          references.reduce(
            (acc: any, reference: any) => acc.concat(reference.reference_category?.id),
            []
          )
        ),
      ]
    : []) as string[];

  const filterCategoryItems = availableReferenceCategories.length
    ? referenceCategories.filter((ref) => availableReferenceCategories.includes(ref?.id))
    : locationTypes;

  const filterLocationTypesItems = availableLocationTypes.length
    ? locationTypes.filter((locationType) => availableLocationTypes.includes(locationType?.id))
    : locationTypes;

  return (
    <LayoutPage {...pageProperties}>
      <LayoutPageHeading
        titleKey="page/professionals/projects/listing/title"
        sublineKey="page/professionals/projects/listing/description"
      />

      <div className={styles.container}>
        <div>
          <FormFilters
            className={styles.filters}
            resultsCount={references.length}
            resultsCountKey="page/professionals/projects/listing/articles_count"
            form={form}
            onChange={(f) => {
              const filters = {};
              for (const property in f) {
                filters[property] = f[property].map((c) => c?.id);
              }
              setFilter(filters);
            }}
            filters={{
              categories: {
                labelKey: 'page/professionals/projects/listing/filter_category',
                items: filterCategoryItems,
              },
              locationTypes: {
                labelKey: 'page/professionals/projects/listing/filter_room',
                items: filterLocationTypesItems,
              },
            }}
          />
        </div>

        <ArticleListingOverview
          articles={references.map((reference: any) => ({ ...reference.article, reference }))}
          link={ArticleLinkPrefix.reference}
          size={LayoutCardSize.Small}
          oneRow={false}
        />
        {/* {!!references.length && (
          <LayoutCardList
            type={LayoutCardType.Article}
            size={LayoutCardSize.Small}
            items={references.map((reference) => ({
              image: reference.assets.length ? reference.assets[0].file : null,
              slug: reference.article.slug,
              link: 'professionals/projects',
              title: reference.article.title,
              caption: reference.reference_category ? reference.reference_category.name : '',
              // footer: product.family,
            }))}
          />
        )} */}
      </div>
    </LayoutPage>
  );
};

export default ProfessionalsReferencesListingPage;
